import { PropsWithChildren, useRef, useState } from 'react';

import { Box, Button, Card, VisuallyHidden } from '@chakra-ui/react';
import { CopyIcon } from '@udacity/chakra-uds-icons';

import { logger } from '~/features/logging/logger';

type CopyToTextProps = PropsWithChildren<{ name: string; text: string; label: string }>;

export function CopyToText({ name, text, label, children }: CopyToTextProps) {
  const [copied, setCopied] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const handleCopyText = async (ev: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.log(err);
      }
    } else {
      if (textAreaRef.current) {
        textAreaRef.current.select();

        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          logger.error('Error copying text', {
            file: 'src/features/reimbursement/components/copy-to-text.tsx',
            function: 'handleCopyText',
            error: err,
          });
        }
      }
    }
  };

  return (
    <Card
      border='1px'
      borderColor='gray.300'
      boxShadow='none'
      display='flex'
      gap='24px'
      padding={{ base: '16px', md: '48px' }}
      variant='outline'
    >
      <Box display='flex' flexDirection='column' gap='16px' id={name}>
        {children}
      </Box>
      <VisuallyHidden>
        <label htmlFor={name}>{label}</label>
        <textarea ref={textAreaRef} id={name} value={text} readOnly />
      </VisuallyHidden>
      <Button
        colorScheme='blue'
        leftIcon={<CopyIcon height='25px' width='25px' />}
        variant='solid'
        width={{ base: '100%', md: '200px' }}
        onClick={handleCopyText}
      >
        {copied ? 'Copied!' : 'Copy Text'}
      </Button>
    </Card>
  );
}
